import { QueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';

// Phase 2 experiment flag (dev/prod-safe)
const refocusEnv = (import.meta.env as any)?.VITE_RQ_EXPERIMENT_REFOCUS;
const isExperimentRefocus =
  String(refocusEnv ?? '').toLowerCase() === 'true' || refocusEnv === '1';

/**
 * Centralized React Query client configuration with robust error handling
 * and retry logic for all data fetching operations.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry failed requests with exponential backoff
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status?: number } | undefined)?.status ?? 0;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        
        // Retry up to 3 times with exponential backoff
        return failureCount < 3;
      },
      
      // Exponential backoff: 1s, 2s, 4s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Stale time: data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      
      // Cache time: keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      
      // Phase 2 experiment: allow native refetch on focus if flag enabled
      refetchOnWindowFocus: isExperimentRefocus ? true : false,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Error handling is done at the query level
    },
    
    mutations: {
      // Retry failed mutations once
      retry: 1,
      
      // Error handling is done at the mutation level
    },
  },
});

// Dev-only: subscribe to query cache changes and log high-level state
if (import.meta.env.DEV && typeof window !== 'undefined') {
  try {
    const flagKey = '__RQ_CACHE_SUB__';
    const w: any = window;
    if (!w[flagKey]) {
      const unsubscribe = queryClient.getQueryCache().subscribe((event: unknown) => {
        try {
          const e = event as { type?: string; query?: { queryKey?: unknown[]; state?: { status?: unknown; fetchStatus?: unknown; isInvalidated?: boolean } ; getObserversCount?: () => number; observers?: unknown[] } };
          const q = e?.query;
          if (!q?.queryKey || !q?.state) return;
          const observersCount = typeof q.getObserversCount === 'function'
            ? q.getObserversCount()
            : Array.isArray(q.observers) ? q.observers.length : undefined;
          logger.info('RQ_CACHE', {
            eventType: e?.type,
            queryKey: q.queryKey,
            status: q.state?.status,
            fetchStatus: (q.state as { fetchStatus?: unknown })?.fetchStatus,
            isStale: (q as { isStale?: boolean })?.isStale ?? undefined,
            observersCount
          });
        } catch {
          // swallow log errors in dev
        }
      });
      // Store unsubscribe in window so HMR can call it if needed
      (w as Record<string, unknown>)[flagKey] = unsubscribe;
    }
  } catch {
    // ignore subscription errors in dev
  }
}

/**
 * Custom error types for better error handling
 */
export class QueryError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'QueryError';
  }
}

/**
 * Utility function to create query keys with consistent structure
 */
export const createQueryKey = (base: string, params?: Record<string, unknown>) => {
  return params ? [base, params] : [base];
};

/**
 * Utility function to handle Supabase errors consistently
 */
export const handleSupabaseError = (error: unknown): QueryError => {
  const err = error as { code?: string; message?: string; status?: number };
  if (err?.code) {
    return new QueryError(
      err.message || 'Database operation failed',
      err.status,
      err.code
    );
  }
  
  return new QueryError(
    err?.message || 'Unknown error occurred',
    err?.status
  );
};

/**
 * Predefined query keys for common operations
 */
export const queryKeys = {
  // User and authentication
  user: () => ['user'],
  userProfile: (userId: string) => ['user', 'profile', userId],
  subscription: (userId: string) => ['user', 'subscription', userId],
  
  // Threads and sessions
  threads: () => ['threads'],
  thread: (threadId: string) => ['thread', threadId],
  threadMessages: (threadId: string) => ['thread', threadId, 'messages'],
  
  // Sessions
  sessions: () => ['sessions'],
  session: (sessionId: string) => ['session', sessionId],
  sessionDetails: (sessionId: string) => ['session', sessionId, 'details'],
  
  // Supervision and alerts
  supervisionAlerts: (threadId: string) => ['supervision', 'alerts', threadId],
  mentorFeedback: (sessionId: string) => ['mentor', 'feedback', sessionId],
  
  // Dashboard data
  dashboardStats: () => ['dashboard', 'stats'],
  dashboardThreads: (userId: string) => ['dashboard', 'threads', userId],
  dashboardSessions: (userId: string) => ['dashboard', 'sessions', userId],
} as const; 